import { inject, Injectable } from '@angular/core';
import { PDFDocument } from 'pdf-lib';
import { BsApiService } from '../API/bs-api.service';
import { S3UploadService } from './s3-upload.service';

export interface FileToPdfOptions {
  accept?: string;
  multiple?: boolean;
}

export interface UploadUrlPayload {
  payload: UploadUrlResponse;
}

export interface UploadUrlResponse {
  uploadURL: string;
  document: {
    documentId: string;
    key: string;
    status: string;
    createdDate: string;
  }
}

/**
 * Global utility for:
 * - selecting files
 * - converting images + PDFs into a single combined PDF
 */
@Injectable({ providedIn: 'root' })
export class FileUploadService {
  private readonly pageWidth = 612; // US Letter
  private readonly pageHeight = 792; // US Letter

  private readonly _bsApi = inject(BsApiService);
  private readonly _s3UploadService = inject(S3UploadService);

  constructor() {}

  /**
   * High-level helper:
   * - opens a file dialog
   * - converts all selected files (images + PDFs) into a single PDF
   * - returns the PDF bytes (or null if user cancels)
   */
  async selectFilesAndBuildPdf(options: FileToPdfOptions = {}): Promise<Uint8Array | null> {
    const { accept = 'image/*,application/pdf', multiple = true } = options;

    const fileList = await this.openFilePicker(accept, multiple);
    if (!fileList?.length) {
      return null; // user cancelled / closed dialog
    }

    const files = Array.from(fileList);
    return this.buildCombinedPdf(files);
  }

  /**
   * Core engine:
   * build a single PDF from image + PDF files.
   */
  async buildCombinedPdf(files: File[]): Promise<Uint8Array> {
    const pdfDoc = await PDFDocument.create();

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        await this.addImageFileToPdf(pdfDoc, file);
      } else if (file.type === 'application/pdf') {
        await this.addPdfFileToPdf(pdfDoc, file);
      } else {
        console.warn('Unsupported file type skipped:', file.name, file.type);
      }
    }

    return pdfDoc.save();
  }

  openFilePicker(accept = '*/*', multiple = false): Promise<FileList | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.multiple = multiple;
      input.style.display = 'none';

      input.addEventListener('change', () => {
        resolve(input.files);
        input.remove();
      });

      document.body.appendChild(input);
      input.click();
    });
  }

  downloadPdf(pdfBytes: Uint8Array, filename: string): void {
    const blob = new Blob([pdfBytes as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  getDocumentUploadURL(documentKey: string, entityId: string, entityType: string, ext: string) {
    return this._bsApi.post<UploadUrlPayload>('api-mobile', '/document', {
      documentKey,
      entityId,
      entityType,
      metadata: {
        ext
      }
    });
  }

  uploadDocument(uploadURL: string, pdfBytes: Uint8Array) {
    return this._s3UploadService.uploadPdfToPresignedUrl(uploadURL, pdfBytes);
  }

  // ----- internals -----

  private async addImageFileToPdf(pdfDoc: PDFDocument, file: File): Promise<void> {
    const imageBytes = await this.imageFileToJpegBytes(file, this.pageWidth, this.pageHeight);

    const img = await pdfDoc.embedJpg(imageBytes);
    const page = pdfDoc.addPage([this.pageWidth, this.pageHeight]);

    page.drawImage(img, {
      x: 0,
      y: 0,
      width: this.pageWidth,
      height: this.pageHeight,
    });
  }

  private async addPdfFileToPdf(pdfDoc: PDFDocument, file: File): Promise<void> {
    const bytes = await file.arrayBuffer();
    const srcPdf = await PDFDocument.load(bytes);
    const copiedPages = await pdfDoc.copyPages(srcPdf, srcPdf.getPageIndices());
    copiedPages.forEach((p) => pdfDoc.addPage(p));
  }

  private async imageFileToJpegBytes(
    file: File,
    pageWidth: number,
    pageHeight: number,
    margin = 24 // ← 24pt on all sides
  ): Promise<Uint8Array> {
    const dataUrl = await this.readFileAsDataURL(file);
    const img = await this.loadImage(dataUrl);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not get canvas 2D context');

    canvas.width = pageWidth;
    canvas.height = pageHeight;

    // Fill entire page with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    // Compute inner drawable area
    const innerWidth = pageWidth - margin * 2;
    const innerHeight = pageHeight - margin * 2;

    // Preserve image aspect ratio
    const imgAspect = img.width / img.height;
    const innerAspect = innerWidth / innerHeight;

    let drawWidth: number;
    let drawHeight: number;

    if (imgAspect > innerAspect) {
      // Image is wider → fit width
      drawWidth = innerWidth;
      drawHeight = innerWidth / imgAspect;
    } else {
      // Image is taller → fit height
      drawHeight = innerHeight;
      drawWidth = innerHeight * imgAspect;
    }

    const offsetX = margin + (innerWidth - drawWidth) / 2;
    const offsetY = margin + (innerHeight - drawHeight) / 2;

    // Draw resized image inside margin box
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

    // Convert canvas to JPEG
    const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const byteString = atob(jpegDataUrl.split(',')[1]);
    const len = byteString.length;
    const bytes = new Uint8Array(len);

    for (let i = 0; i < len; i++) {
      bytes[i] = byteString.charCodeAt(i);
    }

    return bytes;
  }

  private readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error);
      reader.onload = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = src;
    });
  }
}
